import * as React from 'react';
import Step, { PrivateStepState, SharedStepState, StepProperties } from '../Step';
import {
    FullStreamingIcon,
    LucidlinkCustom,
    LucidlinkPerformance,
    LucidlinkStandard,
    RightArrowIcon
} from '../../common/Icons';
import { withMapBackground } from '../../hocs/MapBackgroundEnhancer';
import { TransitionType } from '../../../utils/types';

export enum ProviderPlanType {
    LucidLinkBasic = 'Lucidlink Classic Basic',
    LucidLinkTeams = 'LucidLink Teams',
    LucidLinkEnterprise = 'LucidLink Enterprise',
    LucidLinkAdvancedAWS = 'LucidLink Classic Enterprise (AWS)',
    LucidLinkAdvancedIBM = 'LucidLink Classic Advanced (IBM)',
    LucidLinkCustom = 'LucidLink Classic Custom'
}

export enum FilespaceType {
    BasicFilespace = 'LucidLink Classic Basic (Wasabi)',
    TeamFilespace = 'Team Filespace',
    EnterpriseFilespace = 'Enterprise Filespace',
    AdvancedFilespaceAWS = 'LucidLink Classic Enterprise (AWS)',
    AdvancedFilespaceIBM = 'LucidLink Classic Advanced (IBM)',
    CustomFilespace = 'LucidLink Classic Custom'
}

export const filespacePlanTypeMap = {
    [ProviderPlanType.LucidLinkBasic]: FilespaceType.BasicFilespace,
    [ProviderPlanType.LucidLinkTeams]: FilespaceType.TeamFilespace,
    [ProviderPlanType.LucidLinkEnterprise]: FilespaceType.EnterpriseFilespace,
    [ProviderPlanType.LucidLinkAdvancedAWS]: FilespaceType.AdvancedFilespaceAWS,
    [ProviderPlanType.LucidLinkAdvancedIBM]: FilespaceType.AdvancedFilespaceIBM,
    [ProviderPlanType.LucidLinkCustom]: FilespaceType.CustomFilespace,
};

interface PlanStepProperties extends StepProperties<PlanSharedStepState> {
    transitionType: TransitionType;
    isMetadataStreamingCheckboxDisabled: boolean;
}

export interface PlanSharedStepState extends SharedStepState {
    providerPlanType: ProviderPlanType | null;
}

enum FeatureIncluded {
    Yes,
    No
}

interface CardFeature {
    included: FeatureIncluded;
    name: string;
}

interface PlanPrivateStepState extends PrivateStepState {
    showHiddenCards: boolean;
}

interface PlanStepState extends PlanPrivateStepState, PlanSharedStepState {
}

class PlanStepComponent extends Step<PlanStepProperties, PlanStepState, PlanSharedStepState> {

    constructor(props: any) {
        super(props);

        this.handleBasicPlanClick = this.handleBasicPlanClick.bind(this);
        this.handleCustomPlanClick = this.handleCustomPlanClick.bind(this);
        this.handleAdvancedAWSPlanClick = this.handleAdvancedAWSPlanClick.bind(this);
        this.handleAdvancedIBMPlanClick = this.handleAdvancedIBMPlanClick.bind(this);
        this.state = {
            ...this.props.savedState,
            showHiddenCards: false,
            providerPlanType: this.props.savedState.providerPlanType || ProviderPlanType.LucidLinkAdvancedAWS
        };

    }

    componentDidUpdate(prevProps: PlanStepProperties) {
        if (this.props.transitionType === prevProps.transitionType) {
            return;
        }
    }

    render() {
        return (
            <div className="col-12 col-lg-8 offset-lg-0 col-xl-8 d-flex align-items-center plan-step">
                <div className="container p-0">
                    <div className="radio-cards">
                        {this.renderPlansList()}
                        <div className="plan_container">
                            {this.renderActivePlanDescription(this.props.transitionType)}
                            {this.renderVersion()}
                        </div>
                    </div>
                    <button onClick={this.completeStep} type="button" className="btn step-btn float-left">
                        <span>Next</span>
                        <RightArrowIcon width="13px" height="13px"/>
                    </button>
                </div>
            </div>
        );
    }

    protected getSharedStepState(): PlanSharedStepState {
        return {
            providerPlanType: this.state.providerPlanType,
        };
    }

    private static renderPlanDescription(
        cardClass: string,
        headerIcon: (props: any) => JSX.Element,
        filespaceType: FilespaceType,
        storageIncludedHeaderLabel: string,
        perTbMoPrice: number,
        egressFeeLabel: string,
        features: CardFeature[],
        transitionType: TransitionType
    ) {
        const HeaderIcon = headerIcon;
        const span = transitionType === TransitionType.V3ToV4 ? null :
            (
                <span className="label">
                    <FullStreamingIcon width="17px" height="17px"/>
                    <span className="ttip">
                        <em>Full streaming support</em>
                        <div>
                            <p>Full streaming support:</p>
                            <ul>
                                <li>Instantly browse a Filespace of any size.</li>
                                <li>Instantly access and browse snapshots of any size.</li>
                                <li>State of the art distributed file locking.</li>
                                <li>Optimized usage of local resources.</li>
                            </ul>
                        </div>
                    </span>
                </span>
            );
        return (
            <div className={`radio-card ${cardClass} desktop`}>
                <div>
                    <header>
                        {span}
                        <div className="icon">
                            <HeaderIcon width="26px" height="26px"/>
                        </div>
                        <h4>{filespaceType}</h4>
                        <p>{storageIncludedHeaderLabel}</p>
                    </header>
                    <section>
                        <div>
                            <div className="left">
                                <strong>Storage</strong>
                                per TB / month
                            </div>
                            <div className="right price">
                                {perTbMoPrice}
                            </div>
                        </div>
                        <div>
                            <div className="left">
                                {egressFeeLabel}
                            </div>
                        </div>
                    </section>
                </div>
                <div className="grey pros_cons">
                    <strong>Features</strong>
                    <ul>
                        {features.map((feature) => {
                            if (feature.included === FeatureIncluded.Yes) {
                                return (<li key={feature.name}>{feature.name}</li>);
                            } else {
                                return (<li key={feature.name} className="cons">{feature.name}</li>);
                            }
                        })}
                    </ul>
                </div>
            </div>
        );
    }

    private static getPlanOptionClassList(isActive: boolean): string {
        return isActive ? 'active' : '';
    }

    private static renderPlanOption(
        isActive: boolean,
        onClick: () => void,
        inputId: string,
        filespaceType: FilespaceType,
        label: string,
        disabled: boolean = false,
    ): JSX.Element {
        const radioDisabledClassName = disabled ? 'radio-disabled' : '';
        const className = PlanStepComponent.getPlanOptionClassList(isActive) + ' ' + radioDisabledClassName;
        return (
            <li className={className}>
                <input
                    onClick={onClick}
                    type="radio"
                    name="radio-list"
                    id={inputId}
                    defaultChecked={isActive}
                    disabled={disabled}
                />
                {disabled ? (
                    <span>
                        <label htmlFor={inputId}>
                            <span><strong>{filespaceType}</strong></span>
                            {label}
                        </label>
                        <div className="radio-disabled-label">
                            Temporarily unavailable
                        </div>
                    </span>
                ) : (
                    <label htmlFor={inputId}>
                        <strong>{filespaceType}</strong>
                        {label}
                    </label>
                )}
            </li>
        );
    }

    private static renderCustomPlanDescription(transitionType: TransitionType) {
        return PlanStepComponent.renderPlanDescription(
            'blue-card', LucidlinkCustom,
            FilespaceType.CustomFilespace,
            'Storage not included',
            40,
            'Provider may charge egress fees',
            [
                {included: FeatureIncluded.Yes, name: 'Default snapshots'},
                {included: FeatureIncluded.Yes, name: 'Global file locking'},
                {included: FeatureIncluded.Yes, name: 'Custom snapshots'},
                {included: FeatureIncluded.Yes, name: 'SSO integration'},
                {included: FeatureIncluded.Yes, name: 'Premium support'},
            ],
            transitionType);
    }

    private static renderAdvancedAWSPlanDescription(transitionType: TransitionType) {
        return PlanStepComponent.renderPlanDescription(
            'orange-card',
            LucidlinkPerformance,
            FilespaceType.AdvancedFilespaceAWS,
            'AWS storage included',
            96,
            'No egress fees',
            [
                {included: FeatureIncluded.Yes, name: 'Default snapshots'},
                {included: FeatureIncluded.Yes, name: 'Global file locking'},
                {included: FeatureIncluded.Yes, name: 'Custom snapshots'},
                {included: FeatureIncluded.Yes, name: 'SSO integration'},
                {included: FeatureIncluded.Yes, name: 'Premium support'},
            ],
            transitionType);

    }

    private static renderAdvancedIBMPlanDescription(transitionType: TransitionType) {
        return PlanStepComponent.renderPlanDescription(
            'orange-card',
            LucidlinkPerformance,
            FilespaceType.AdvancedFilespaceIBM,
            'IBM storage included',
            80,
            'No egress fees',
            [
                {included: FeatureIncluded.Yes, name: 'Default snapshots'},
                {included: FeatureIncluded.Yes, name: 'Global file locking'},
                {included: FeatureIncluded.Yes, name: 'Custom snapshots'},
                {included: FeatureIncluded.Yes, name: 'SSO integration'},
                {included: FeatureIncluded.Yes, name: 'Premium support'},
            ],
            transitionType);

    }

    private static renderBasicPlanDescription(transitionType: TransitionType) {
        return PlanStepComponent.renderPlanDescription(
            'grey-card',
            LucidlinkStandard,
            FilespaceType.BasicFilespace,
            'Wasabi storage included',
            20,
            'No egress fees',
            [
                {included: FeatureIncluded.Yes, name: 'Default snapshots'},
                {included: FeatureIncluded.Yes, name: 'Global file locking'},
                {included: FeatureIncluded.No,  name: 'Custom snapshots'},
                {included: FeatureIncluded.No,  name: 'SSO integration'},
                {included: FeatureIncluded.Yes, name: 'Standard support'}
            ],
            transitionType);
    }

    private renderActivePlanDescription(transitionType: TransitionType): JSX.Element | null {
        switch (this.state.providerPlanType) {
            case ProviderPlanType.LucidLinkBasic:
                return PlanStepComponent.renderBasicPlanDescription(transitionType);
            case ProviderPlanType.LucidLinkAdvancedAWS:
                return PlanStepComponent.renderAdvancedAWSPlanDescription(transitionType);
            case ProviderPlanType.LucidLinkAdvancedIBM:
                return PlanStepComponent.renderAdvancedIBMPlanDescription(transitionType);
            case ProviderPlanType.LucidLinkCustom:
                return PlanStepComponent.renderCustomPlanDescription(transitionType);
            default:
                return null;
        }
    }

    private renderPlansList() {
        return (
            <ul className="radio-list">
                {PlanStepComponent.renderPlanOption(
                    this.isPlanActive(ProviderPlanType.LucidLinkAdvancedAWS),
                    this.handleAdvancedAWSPlanClick,
                    'advancedAWS',
                    FilespaceType.AdvancedFilespaceAWS,
                    'High-performance workloads')}
                {PlanStepComponent.renderPlanOption(
                    this.isPlanActive(ProviderPlanType.LucidLinkAdvancedIBM),
                    this.handleAdvancedIBMPlanClick,
                    'advancedIBM',
                    FilespaceType.AdvancedFilespaceIBM,
                    'High-performance workloads')}
                {PlanStepComponent.renderPlanOption(
                    this.isPlanActive(ProviderPlanType.LucidLinkCustom),
                    this.handleCustomPlanClick,
                    'custom',
                    FilespaceType.CustomFilespace,
                    'Storage-dependent workloads')}
                {PlanStepComponent.renderPlanOption(
                    this.isPlanActive(ProviderPlanType.LucidLinkBasic),
                    this.handleBasicPlanClick,
                    'basic',
                    FilespaceType.BasicFilespace,
                    '',
                    true)}
            </ul>
        );
    }

    private renderVersion() {
        if (this.state.providerPlanType === ProviderPlanType.LucidLinkAdvancedAWS) {
            return (
                <span className="underlined">
                    Lucid client <a href="https://lucidlink.com/download-lucidlink-classic" target="_blank">
                    version 2.8 or higher</a> required.
                </span>
            );
        } else {
            return (
                <span className="underlined">&nbsp;</span>
            );
        }
    }

    private handleBasicPlanClick(): void {
        this.handlePlanSelected(ProviderPlanType.LucidLinkBasic);
    }

    private handleAdvancedAWSPlanClick(): void {
        this.handlePlanSelected(ProviderPlanType.LucidLinkAdvancedAWS);
    }

    private handleAdvancedIBMPlanClick(): void {
        this.handlePlanSelected(ProviderPlanType.LucidLinkAdvancedIBM);
    }

    private handleCustomPlanClick(): void {
        this.handlePlanSelected(ProviderPlanType.LucidLinkCustom);
    }

    private handlePlanSelected(type: ProviderPlanType): void {
        this.setState({providerPlanType: type});
    }

    private isPlanActive(planType: ProviderPlanType): boolean {
        return this.state.providerPlanType === planType;
    }
}

const planStepComponentWithBackground = withMapBackground(PlanStepComponent, true);
export { planStepComponentWithBackground as PlanStepComponent };



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/components/PlanStep.tsx